/**
 * The layers panel.
 *
 * Phase 1 made it operable by mouse, finger and keyboard. Phase 2 makes it
 * readable: a thumbnail per layer, a name you can edit in place, and a drop
 * indicator that shows where a dragged layer will land.
 *
 * The decisions worth knowing:
 *
 *   **It reconciles; it does not rebuild.** `render()` is called from the
 *   stack's `onChange`, which fires on every recomposite — during a stroke that
 *   is once a frame. Rebuilding `innerHTML` at that rate would re-create a dozen
 *   thumbnail canvases per frame, drop focus mid-keystroke, and wipe a rename
 *   the moment the brush touched the canvas. So rows are created once, kept in a
 *   map, and updated in place.
 *
 *   **Thumbnails are canvases, not encoded images.** A `<canvas>` per row costs
 *   one `drawImage` to refresh. `toDataURL` would encode a PNG per layer per
 *   refresh to display something 32 pixels wide.
 *
 *   **Pointer events, not HTML5 drag-and-drop**, because `dragstart` never fires
 *   for touch and this audience draws on iPads. Dragging is from a grab handle
 *   after a long-press, so the list still scrolls under a finger.
 *
 *   **Every drag has a menu equivalent.** WCAG 2.2 SC 2.5.7 requires a
 *   single-pointer path for anything achievable by dragging.
 */
import { BLEND_MODE_LABELS, BLEND_MODES, type GarmentType, isBlendMode } from "../../shared/format";
import { describe as describeSelection, EMPTY, type Selection } from "../../shared/selection";
import { MAX_TILE_PX, MIN_TILE_PX } from "./fabric-fill";
import { fabricOf } from "./fabric-source";
import { faceLabel } from "./faces";
import { type LayerStack, planMerge, type RuntimeLayer } from "./layers";
import { type MenuItem, showMenu } from "./menu";

export interface PanelOptions {
  stack: LayerStack;
  garment?: GarmentType;
  /**
   * Open the colour picker for a layer made from a fabric.
   *
   * The panel decides *whether* a row can be recoloured, because that is a
   * property of the layer it is already rendering. It does not decide what
   * happens next: picking a colour needs the fabric library, the entitlement
   * gate and a re-fill, none of which belong in a list widget.
   */
  onRecolour?: (id: string) => void;
  /**
   * Re-fill a fabric layer at a coarser or finer weave.
   *
   * Same split as `onRecolour`: the panel knows which rows are still made of a
   * fabric, because it is already rendering that. What a re-fill costs — the
   * tile, the entitlement, the history entry — is not a list widget's business.
   */
  onWeave?: (id: string, direction: 1 | -1) => void;
  /**
   * Swap the pixels of a placement, keeping where it sits.
   *
   * Same split as the two above: the panel knows which rows are placements
   * because it is already rendering that, and knows nothing about decoding a
   * file. What makes this worth a menu item rather than "delete and place
   * again" is that the transform survives — a badge positioned and rotated onto
   * a sleeve keeps all of that when the artwork behind it changes.
   */
  onReplace?: (id: string) => void;
  /**
   * Soften a layer's pixels.
   *
   * Same split as the others: the panel knows which row was asked, and knows
   * nothing about canvas filters, history entries or what a radius means on a
   * placement.
   */
  onBlur?: (id: string) => void;
  /**
   * The selected layer changed.
   *
   * `stack.setActive` only assigns — it does not composite and it notifies
   * nobody, so anything drawing FOR the selection has no way to know. The
   * transform handles are exactly that: selecting a placement left the canvas
   * unchanged until something else happened to recomposite, so clicking a row
   * appeared to do nothing.
   *
   * Reported from here because the panel is where selection happens; adding a
   * second notifier to the stack would mean two places that can disagree about
   * what is selected.
   */
  onSelect?: (id: string | null) => void;
  /**
   * The faces picked right now, for "Only these faces" on a layer row.
   *
   * The panel reads it rather than owning it: there is one selection and one
   * picker, and a row menu that kept its own copy would be a second place for
   * them to disagree.
   */
  selection?: () => Selection;
  /**
   * Extra commands for a layer's menu, supplied by whoever owns them.
   *
   * The mirror one-shots arrive this way. They rewrite half of one layer in
   * place, which makes them siblings of Duplicate and Merge down rather than of
   * the tools — but they need a body and a mirror map, so they cannot be built
   * here. The panel asks; it does not know what they are.
   */
  layerItems?: () => MenuItem[];
  /**
   * Where the blend and opacity controls should live.
   *
   * Passed in rather than found, because the panel would otherwise have to know
   * the shell's markup to reach outside its own host. Given the dock header,
   * these two controls cost no vertical space at all; without it they fall back
   * to a strip above the list, which is correct but is a band the list pays for.
   */
  propsSlot?: HTMLElement;
}

/** Long enough not to fire on a scroll flick, short enough not to feel stuck. */
const LONG_PRESS_MS = 400;
/** Movement past this before the timer fires means the user is scrolling. */
const SCROLL_SLOP_PX = 8;
/** How close to the list edge a drag must get before it starts scrolling. */
const EDGE_ZONE_PX = 48;
/** Fast enough to cross a long list, slow enough to stop where you meant. */
const MAX_SCROLL_PX_PER_FRAME = 12;
/** Thumbnails settle after the pointer stops rather than chasing every frame. */
const THUMB_DEBOUNCE_MS = 120;
/** CSS pixels. Backed at device resolution so it is not soft on a phone. */
const THUMB = 32;

/** Where a layer lives, in one phrase. Blank for artwork, which is everywhere. */
function whereLabel(layer: RuntimeLayer, garment: GarmentType, clipped = false): string {
  const parts: string[] = [];
  // First, because it changes what everything after it means.
  if (clipped) parts.push("Clipped");
  if (layer.kind === "placement" && layer.placement) {
    parts.push(
      `${faceLabel(layer.placement.face, garment)} · ${Math.round(layer.placement.scale * 100)}%`,
    );
  } else if (layer.link) {
    parts.push(layer.link.filename);
  }

  /*
    Blend and opacity, but only when they are not the default.

    The strip above the list is where these are SET; this is the only place they
    are visible without selecting the layer first, which is what makes a stack
    readable at a glance — "why is that one grey" is answered by the row rather
    than by clicking through every layer.

    Silent at Normal and 100% on purpose. A row that said "Normal · 100%" on
    every layer would be noise on exactly the layers that have nothing to say,
    and this line is about eighteen characters wide before it truncates.
  */
  if (layer.blendMode !== "normal") parts.push(BLEND_MODE_LABELS[layer.blendMode]);
  if (layer.opacity < 1) parts.push(`${Math.round(layer.opacity * 100)}%`);

  return parts.join(" · ");
}

/**
 * Where a dragged layer lands, given the gap the indicator was sitting in.
 *
 * Pure and exported because this is the one calculation in the panel that can
 * be *silently* wrong: a sign error puts every layer one place off, which looks
 * like a flaky drag rather than a bug, and the drop is the moment the user is
 * least able to tell what happened.
 *
 * Two conventions meet here and they run opposite ways. `order` is the stack,
 * bottom-first, because that is paint order. The panel draws top-first, because
 * that is how every layers panel has worked since 1994. So the gap **above** a
 * row is the position **after** that layer in the array.
 *
 * Indices are into the array *with the dragged layer already removed*, which is
 * what `moveWithin`'s remove-then-insert does — computing against the original
 * array is the off-by-one this function exists to prevent.
 *
 * Returns null when the drop changes nothing or refers to a layer that is gone.
 */
export function dropIndex(
  order: readonly string[],
  dragId: string,
  targetId: string,
  above: boolean,
): number | null {
  if (dragId === targetId) return null;
  const remaining = order.filter((id) => id !== dragId);
  const anchor = remaining.indexOf(targetId);
  if (anchor === -1) return null;
  return above ? anchor + 1 : anchor;
}

interface Row {
  el: HTMLElement;
  thumb: HTMLCanvasElement;
  main: HTMLButtonElement;
  name: HTMLElement;
  where: HTMLElement;
  eye: HTMLButtonElement;
  /**
   * The row's overflow menu.
   *
   * Held so its accessible name can follow a rename. It draws as three dots
   * and carried no name at all, so every row announced an identical bare
   * "button" — and with one per layer, that is a list of them.
   */
  more: HTMLButtonElement;
  /** Shown only while the layer is still made of a fabric. */
  tint: HTMLButtonElement;
  /** "Paint inside only" — a state, so it shows in the row rather than a menu. */
  inside: HTMLButtonElement;
  /** "Only these faces" — likewise; a restricted layer must not look unrestricted. */
  only: HTMLButtonElement;
  /** The layer's `rev` at the last thumbnail redraw. */
  drawnRev: number;
  /** Device pixel ratio the thumbnail was sized for. */
  drawnDpr: number;
  /**
   * What this row last displayed.
   *
   * `render()` runs once per frame during a stroke. Without this, every frame
   * re-parses two SVGs and rewrites six attributes per layer to the values they
   * already hold — work that is invisible in a profile until someone paints on
   * a phone with twelve layers open.
   */
  shown: {
    name: string;
    where: string;
    visible: boolean;
    /** Last-drawn state of the two row toggles, so they repaint only on change. */
    insideOnly: boolean;
    /** The face limit as a stable key, or "" for none. */
    onlyFaces: string;
    active: boolean;
    kind: string;
    /** The tint last painted on the dot, or null for "as it comes". */
    tint: string | null;
    /** Whether the dot was shown at all. */
    tintable: boolean;
  };
  /**
   * Forces the first update to write everything.
   *
   * A cache seeded with plausible defaults is a trap: a visible layer matched
   * `visible: true` on the very first pass, the eye's icon was never written,
   * and every row shipped with an empty button where the eye should be. There
   * is no "impossible" default for a boolean, so the first pass is flagged
   * rather than guessed at.
   */
  first: boolean;
}

const PROPS_STYLE_ID = "layer-props-style";

/*
  Injected here rather than added to hud.css, which another session has open.
  Every value is a token — the spec in docs/decisions/layer-blending.md lists
  them, and all fifteen were checked against the generated stylesheet.
*/
export const PROPS_CSS = `
/*
  One row, because this sits between the panel header and the layers.

  It began as a stacked column — a full-width select, then a labelled slider on
  its own line — which came to about 100px before the first layer row. On a
  panel that is often only a few hundred pixels tall that is most of the list,
  spent on two controls that fit side by side. Everything here is in service of
  keeping it to a single line that ends up roughly a third of that.
*/
.layer-props {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  padding: var(--space-2xs) var(--space-sm);
  border-block-end: var(--border-width) solid var(--panel-border);
}
.layer-props[hidden] { display: none; }
.layer-blend {
  /* Shrinkable, but not below what "Soft light" needs to stay readable. */
  flex: 0 1 auto;
  inline-size: 108px;
  min-inline-size: 84px;
  block-size: var(--control-height-sm);
  font-size: var(--type-size-xs);
  font-weight: var(--type-weight-normal);
  color: var(--control-text);
  background: var(--control-bg);
  border: var(--border-width) solid var(--control-border);
  border-radius: var(--radius-sm);
  padding-inline: var(--space-2xs);
}
.layer-blend:disabled { opacity: var(--disabled-opacity); }
.layer-blend:focus-visible,
.layer-opacity input:focus-visible {
  outline: var(--focus-width) solid var(--focus-ring);
  outline-offset: var(--focus-offset);
}
.layer-opacity {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  /* Takes the rest of the row. The zero min-inline-size is what lets it, and
     the slider inside it, shrink past their content width in a flex row. */
  flex: 1 1 auto;
  min-inline-size: 0;
  font-size: var(--type-size-xs);
  color: var(--text-muted);
}
.layer-opacity input {
  flex: 1 1 auto;
  min-inline-size: 0;
  margin: 0;
}
.layer-opacity__value {
  flex: 0 0 auto;
  /* Fixed width and tabular figures, so the slider does not resize itself as
     the number goes from 100% to 9%. */
  min-inline-size: 34px;
  text-align: end;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}
/*
  In the dock header, where it costs the list nothing.

  No border and no block padding: the header already has both, and the strip is
  a passenger in a row that exists rather than a band of its own. The slider is
  capped instead of greedy — stretched across a wide dock it reads as the main
  thing in the panel, which it is not.
*/
.dock-props { display: contents; }
.layer-props--inline {
  padding: 0;
  border-block-end: none;
  flex: 0 1 auto;
  min-inline-size: 0;
}
.layer-props--inline .layer-opacity { flex: 0 1 auto; }
.layer-props--inline .layer-opacity input { max-inline-size: 132px; }
/* The touch floor, matching .layer-row's own coarse-pointer rule. Still one
   row — taller controls, not another line. */
@media (pointer: coarse) {
  .layer-blend { block-size: var(--control-height-lg); }
  .layer-opacity input { block-size: var(--control-height-lg); }
}
`;

export function mountLayersPanel(host: HTMLElement, opts: PanelOptions) {
  const { stack } = opts;
  const garment = opts.garment ?? "shirt";
  /** `describe` knows shirt and pants; GarmentType is wider. */
  const named2 = () => (garment === "pants" ? "pants" : "shirt");

  const rows = new Map<string, Row>();
  /** The layer being renamed, if any. Its name is not touched by render(). */
  let renaming: string | null = null;

  // The drop indicator lives in the list, not on a row. A line drawn *on* the
  // hovered row inherits its rounded corners and reads as an outline of that
  // layer rather than a gap between two — which is exactly what it looked like.
  const dropLine = document.createElement("div");
  dropLine.className = "layer-drop";
  dropLine.setAttribute("aria-hidden", "true");

  const empty = document.createElement("p");
  empty.className = "layer-empty";
  // Teaches what a layer is, rather than reporting that there are none. Plenty
  // of this audience has never used one.
  empty.textContent =
    "Layers stack up — whatever is on top covers what is below. Import a PNG or start drawing.";

  // ----------------------------------------------------------- properties

  /*
    Blend and opacity, for whichever layer is selected.

    NOT on the row. The row already carries eight children, and the CSS records
    what happened the last time somebody added a ninth: "adding a sixth button
    wrapped the row and pushed the eye and the menu onto a second line". A strip
    above the list is also where Photoshop, Clip Studio and Procreate put these,
    so it is the arrangement this audience already knows.

    A SIBLING of the host rather than a child, because `render()` calls
    `host.replaceChildren(empty)` when the stack is empty and would take the
    strip with it.
  */
  if (!document.getElementById(PROPS_STYLE_ID)) {
    const style = document.createElement("style");
    style.id = PROPS_STYLE_ID;
    style.textContent = PROPS_CSS;
    document.head.appendChild(style);
  }

  const props = document.createElement("div");
  props.className = "layer-props";

  const blend = document.createElement("select");
  blend.className = "layer-blend";
  blend.setAttribute("aria-label", "Blend mode");
  for (const mode of BLEND_MODES) {
    const option = document.createElement("option");
    option.value = mode;
    option.textContent = BLEND_MODE_LABELS[mode];
    blend.appendChild(option);
  }

  const opacityRow = document.createElement("label");
  opacityRow.className = "layer-opacity";
  /*
    No visible "Opacity" caption.

    It cost a fixed 46px column on a row that has to hold a select and a slider,
    and a slider sitting next to a percentage does not need to be told what it
    is. The name still reaches a screen reader through the input's `aria-label`,
    and `title` puts it a hover away for anyone unsure.
  */
  opacityRow.title = "Opacity";
  const opacityInput = document.createElement("input");
  opacityInput.type = "range";
  opacityInput.min = "0";
  opacityInput.max = "100";
  opacityInput.step = "1";
  opacityInput.setAttribute("aria-label", "Layer opacity");
  const opacityValue = document.createElement("span");
  opacityValue.className = "layer-opacity__value";
  // A range input announces its own value, so the readout is decoration and
  // saying it twice is worse than not saying it.
  opacityValue.setAttribute("aria-hidden", "true");
  // appendChild one at a time: @cloudflare/workers-types shadows the DOM's
  // variadic ParentNode.append in this project, so it does not typecheck — the
  // same trap this file already documents for the row's children.
  for (const child of [opacityInput, opacityValue]) opacityRow.appendChild(child);

  for (const child of [blend, opacityRow]) props.appendChild(child);
  /*
    Into the header when the shell offers a slot there, which costs the list no
    height. The fallback keeps the panel standalone — it is mounted in tests and
    in the style guide with no dock around it — at the price of a band above the
    list, which is what this used to be everywhere.
  */
  if (opts.propsSlot) {
    props.classList.add("layer-props--inline");
    opts.propsSlot.appendChild(props);
  } else {
    host.parentElement?.insertBefore(props, host);
  }

  /*
    The value the drag started from, so the whole drag is one undo step.

    `input` fires per frame and drives the canvas; `change` fires once, on
    release, and is where history is written — the same shape the transform
    handles use. Recording per frame would put sixty entries on the stack for
    one gesture.
  */
  let opacityFrom: number | null = null;

  opacityInput.addEventListener("input", () => {
    const layer = stack.active();
    if (!layer) return;
    if (opacityFrom === null) opacityFrom = layer.opacity;
    const next = Number(opacityInput.value) / 100;
    opacityValue.textContent = `${Math.round(next * 100)}%`;
    stack.setOpacity(layer.id, next);
  });

  opacityInput.addEventListener("change", () => {
    const layer = stack.active();
    const from = opacityFrom;
    opacityFrom = null;
    if (!layer || from === null) return;
    const to = Number(opacityInput.value) / 100;
    if (from === to) return;
    // Rewound and re-applied, so `transaction` has a before to record — the
    // drag has already moved it, which is what made the preview live.
    stack.setOpacity(layer.id, from);
    stack.transaction("Opacity", () => stack.setOpacity(layer.id, to));
  });

  blend.addEventListener("change", () => {
    const layer = stack.active();
    if (!layer) return;
    const next = isBlendMode(blend.value) ? blend.value : "normal";
    if (next === layer.blendMode) return;
    stack.transaction(`Blend: ${BLEND_MODE_LABELS[next]}`, () =>
      stack.setBlendMode(layer.id, next),
    );
  });

  /** Point the strip at the selected layer. Called from `render`. */
  function renderProps(layers: readonly RuntimeLayer[], activeId: string | null) {
    const layer = activeId ? layers.find((l) => l.id === activeId) : null;
    props.hidden = !layer;
    if (!layer) return;

    if (blend.value !== layer.blendMode) blend.value = layer.blendMode;
    // Not touched mid-drag: writing the slider from state while it is being
    // dragged fights the pointer.
    if (opacityFrom === null) {
      const pct = Math.round(layer.opacity * 100);
      opacityInput.value = String(pct);
      opacityValue.textContent = `${pct}%`;
    }

    /*
      Nothing below means nothing to blend with.

      Disabled and told why, rather than hidden — the row menu already treats
      "Merge down" this way, and a control that vanishes moves everything under
      it every time the selection changes.
    */
    const bottom = layers[0]?.id === layer.id;
    blend.disabled = bottom;
    blend.title = bottom ? "Nothing below to blend with." : "";
  }

  // ------------------------------------------------------------------- rows

  function createRow(layer: RuntimeLayer): Row {
    const el = document.createElement("div");
    el.className = "layer-row";
    el.dataset.layer = layer.id;
    el.setAttribute("role", "listitem");

    const grip = document.createElement("span");
    grip.className = "layer-grip";
    grip.dataset.grip = "";
    grip.setAttribute("aria-hidden", "true");
    grip.title = "Drag to reorder";
    grip.innerHTML = GRIP;

    const thumbWrap = document.createElement("span");
    thumbWrap.className = "layer-thumb";
    const thumb = document.createElement("canvas");
    thumbWrap.appendChild(thumb);

    const main = document.createElement("button");
    main.type = "button";
    main.className = "layer-main";

    const name = document.createElement("span");
    name.className = "layer-name";
    const where = document.createElement("span");
    where.className = "layer-where";
    main.appendChild(name);
    main.appendChild(where);

    /*
      The colour dot.

      A fourth visible target on the row, which keys-and-controls.md caps at
      three — and this is the deviation it allows for, narrowly. The other
      three are generic: select, hide, more. This one edits the property the
      layer is *made of*, and putting it behind the overflow menu would hide
      the only affordance for the thing the row is showing you.

      It appears only on rows that are still a fabric, so most lists never have
      a fourth target at all.
    */
    const tint = document.createElement("button");
    tint.type = "button";
    tint.className = "layer-tint";
    tint.hidden = true;
    tint.addEventListener("click", (e) => {
      // The row's own click selects the layer; recolouring is its own action.
      e.stopPropagation();
      opts.onRecolour?.(layer.id);
    });

    /*
      Two STATES, shown because they are otherwise invisible.

      Both change what happens next without changing a pixel, so a row carrying
      them silently is a row that lies. "Only these faces" was the worse of the
      two: it clips the composite and persists in the file, and a restricted
      layer looked exactly like an unrestricted one.

      States, not commands. The mirror one-shots stay in the menu — there is no
      "on" for them to show, and two buttons per row for something used once a
      design is the trade the menu exists to avoid.
    */
    const inside = document.createElement("button");
    inside.type = "button";
    inside.className = "icon-btn layer-inside";
    inside.innerHTML = INSIDE;

    const only = document.createElement("button");
    only.type = "button";
    only.className = "icon-btn layer-only";
    only.innerHTML = ONLY_FACES;

    inside.addEventListener("click", (e) => {
      e.stopPropagation();
      stack.transaction("Paint inside only", () =>
        stack.setPaintInsideOnly(layer.id, !layer.paintInsideOnly),
      );
      render();
    });

    only.addEventListener("click", (e) => {
      e.stopPropagation();
      const sel = opts.selection?.() ?? EMPTY;
      const limited = (layer.faces?.length ?? 0) > 0;
      // Pressed means restricted, so pressing it again lifts the restriction —
      // and with nothing picked there is nothing to apply, so it says so
      // rather than doing nothing.
      if (!limited && sel.size === 0) return;
      stack.transaction(limited ? "Lift layer face limit" : "Limit layer to faces", () =>
        stack.setFaces(layer.id, limited ? [] : [...sel]),
      );
      render();
    });

    const eye = document.createElement("button");
    eye.type = "button";
    eye.className = "icon-btn layer-eye";

    const more = document.createElement("button");
    more.type = "button";
    more.className = "icon-btn layer-more";
    more.dataset.more = layer.id;
    more.setAttribute("aria-haspopup", "menu");
    more.setAttribute("aria-expanded", "false");
    more.innerHTML = DOTS;

    // appendChild one at a time: @cloudflare/workers-types shadows the DOM's
    // variadic ParentNode.append in this project, so it does not typecheck.
    for (const child of [grip, thumbWrap, main, tint, inside, only, eye, more])
      el.appendChild(child);

    main.addEventListener("click", () => selectAndFocus(layer.id));
    // Double-click to rename is what every file list does; no icon needed, and
    // an icon per row would break the three-targets rule for a rare action.
    main.addEventListener("dblclick", (e) => {
      e.preventDefault();
      beginRename(layer.id);
    });
    eye.addEventListener("click", () => {
      const current = stack.get(layer.id);
      if (!current) return;
      stack.transaction(current.visible ? "Hide layer" : "Show layer", () =>
        stack.setVisible(layer.id, !current.visible),
      );
    });
    more.addEventListener("click", () => openRowMenu(more, layer.id));

    return {
      el,
      thumb,
      main,
      name,
      where,
      tint,
      inside,
      only,
      eye,
      more,
      drawnRev: -1,
      drawnDpr: 0,
      shown: {
        name: "",
        where: "",
        visible: true,
        insideOnly: false,
        onlyFaces: "",
        active: false,
        kind: "",
        tint: null,
        tintable: false,
      },
      first: true,
    };
  }

  /*
    Which layers are ACTUALLY clipped this frame.

    From `clipBaseOf` rather than from the flag, so a layer carrying the flag
    with nothing beneath it does not claim to be clipped. Computed once per
    render rather than per row: it is a walk down the stack each time.
  */
  let clippedIds: Set<string> | null = null;

  function updateRow(row: Row, layer: RuntimeLayer, isActive: boolean) {
    const was = row.shown;
    const where = whereLabel(layer, garment, Boolean(clippedIds?.has(layer.id)));
    const force = row.first;
    row.first = false;

    if (force || was.active !== isActive) {
      row.el.classList.toggle("is-active", isActive);
      // Roving tabindex: one stop for the whole list, arrows move within it.
      row.el.tabIndex = isActive ? 0 : -1;
      // `aria-current`, not `aria-selected`: this is a list whose rows contain
      // their own buttons, which rules out listbox/option semantics.
      if (isActive) row.el.setAttribute("aria-current", "true");
      else row.el.removeAttribute("aria-current");
      was.active = isActive;
    }

    // Skipped while renaming: the name element holds the input, and writing to
    // it would destroy the field mid-keystroke.
    if ((force || was.name !== layer.name) && renaming !== layer.id) {
      row.name.textContent = layer.name;
      was.name = layer.name;
    }

    if (force || was.where !== where) {
      row.where.textContent = where;
      row.where.hidden = where === "";
      was.where = where;
    }

    /*
      The two states, drawn from the layer every time it is refreshed.

      `aria-pressed` and a class, not just a colour: pressed is the only thing
      that reads as "on" to a screen reader, and the class is what lets the two
      look different from a plain icon button at a glance.
    */
    const insideOn = Boolean(layer.paintInsideOnly);
    if (force || was.insideOnly !== insideOn) {
      row.inside.setAttribute("aria-pressed", String(insideOn));
      row.inside.classList.toggle("is-on", insideOn);
      row.inside.setAttribute(
        "aria-label",
        insideOn
          ? `Paint inside only is on for ${layer.name}`
          : `Paint inside only for ${layer.name}`,
      );
      row.inside.title = insideOn
        ? "Strokes stay inside what this layer already has"
        : "Keep strokes inside what this layer already has";
      was.insideOnly = insideOn;
    }

    /*
      Shown only when it can do something.

      A restriction you have not made and cannot make yet has no control worth
      pressing: with nothing picked, this button sat in every row doing nothing,
      which reads as broken rather than as unavailable. It now appears when the
      layer IS restricted — where it is the only sign of that, and one click
      lifts it — or when faces are picked and it can apply them.

      Same shape as the tint dot, which is only on rows that are a fabric. The
      row grows a control when there is one worth growing, and setting it from
      cold stays in the menu, where a disabled row can explain itself.
    */
    const facesKey = (layer.faces ?? []).join(",");
    const pickedNow = (opts.selection?.() ?? EMPTY).size;
    const showOnly = facesKey.length > 0 || pickedNow > 0;
    if (row.only.hidden === showOnly) row.only.hidden = !showOnly;
    if (force || was.onlyFaces !== `${facesKey}|${pickedNow}`) {
      const limited = facesKey.length > 0;
      row.only.setAttribute("aria-pressed", String(limited));
      row.only.classList.toggle("is-on", limited);
      const named = limited ? describeSelection(new Set(layer.faces), named2()) : "";
      row.only.setAttribute(
        "aria-label",
        limited
          ? `${layer.name} shows on ${named} only`
          : `Limit ${layer.name} to the selected faces`,
      );
      /*
        Off, this is only reachable with a selection to apply — so it says what
        to do rather than looking broken when pressing it does nothing.
      */
      row.only.title = limited
        ? `Showing on ${named} only. Click to show on all faces`
        : `Limit this layer to ${describeSelection(opts.selection?.() ?? EMPTY, named2())}`;
      was.onlyFaces = `${facesKey}|${pickedNow}`;
    }

    if (force || was.visible !== layer.visible) {
      row.eye.innerHTML = layer.visible ? EYE : EYE_OFF;
      row.eye.setAttribute("aria-pressed", layer.visible ? "false" : "true");
      row.el.classList.toggle("is-hidden", !layer.visible);
      was.visible = layer.visible;
    }
    // Depends on both, so it is compared separately rather than folded above.
    const label = `${layer.visible ? "Hide" : "Show"} ${layer.name}`;
    if (row.eye.getAttribute("aria-label") !== label) {
      row.eye.setAttribute("aria-label", label);
      /*
        A title as well as a label, because the tip system reads both and the
        panel had neither — every icon in this row was unexplained until you
        clicked it and found out.
      */
      row.eye.title = layer.visible
        ? "Hide this layer. It stays in the file and stops showing on the body"
        : "Show this layer again";
    }

    /*
      Named for the same reason, and it follows a rename for the same reason
      the eye does — the row outlives the name it was built with.
    */
    const moreLabel = `More actions for ${layer.name}`;
    if (row.more.getAttribute("aria-label") !== moreLabel) {
      row.more.setAttribute("aria-label", moreLabel);
      /*
        The tooltip names what is inside, where the label names the button.

        Repeating "More actions for Artwork" into a tooltip tells somebody
        hovering it exactly what they already read. Listing the contents is the
        only version that saves the click, and this menu is now where mirroring
        lives — which nobody would guess from three dots.
      */
      row.more.title = "Rename, limit to faces, mirror halves, duplicate, merge down, delete";
    }

    if (force || was.kind !== layer.kind) {
      row.el.dataset.kind = layer.kind;
      was.kind = layer.kind;
    }

    /*
      The dot, and whether there is one.

      fabricOf() returns null the moment the layer's pixels stop being the ones
      the fill produced, so painting on a fabric makes the dot disappear — which
      is exactly the promise it should make. It is derived, so undo brings it
      back with the pixels.
    */
    const source = fabricOf(layer);
    const tintable = source !== null;
    const tint = source?.tint ?? null;
    if (force || was.tintable !== tintable || was.tint !== tint) {
      row.tint.hidden = !tintable;
      if (source) {
        // A checkerboard for "as it comes", the same convention the swatches
        // and the picker's own clear button already use for "no colour here".
        row.tint.dataset.bare = tint ? "false" : "true";
        row.tint.style.setProperty("--dot", tint ?? "transparent");
        row.tint.setAttribute(
          "aria-label",
          tint ? `Recolour ${source.name}` : `Add a colour to ${source.name}`,
        );
        row.tint.title = tint ? `${source.name} · ${tint}` : `${source.name} · as it comes`;
      }
      was.tintable = tintable;
      was.tint = tint;
    }
  }

  function render() {
    const layers = stack.layers();
    const activeId = stack.active()?.id ?? null;
    clippedIds = new Set(layers.filter((l) => stack.clipBaseOf(l.id)).map((l) => l.id));
    renderProps(layers, activeId);

    if (layers.length === 0) {
      rows.clear();
      host.replaceChildren(empty);
      return;
    }
    if (empty.isConnected) empty.remove();

    // Top of the panel is the top of the stack. The array is bottom-first
    // because that is paint order; this reverse is the only place the two
    // conventions meet.
    const visual = [...layers].reverse();

    visual.forEach((layer, i) => {
      let row = rows.get(layer.id);
      if (!row) {
        row = createRow(layer);
        rows.set(layer.id, row);
      }
      updateRow(row, layer, layer.id === activeId);

      /*
        Only move a row that is not already where it belongs.

        Re-inserting a node that is already in place is not free and is not
        harmless: taking an element out of the document blurs anything focused
        inside it, so an unconditional append would blur the rename field on the
        first frame of the next brush stroke — committing a rename the user was
        halfway through typing.
      */
      if (host.children[i] !== row.el) host.insertBefore(row.el, host.children[i] ?? null);
    });

    const live = new Set(visual.map((l) => l.id));
    for (const [id, row] of rows) {
      if (live.has(id)) continue;
      row.el.remove();
      rows.delete(id);
    }

    // Last child, so it never sits between two rows in the tree. Gated for the
    // same reason the rows are: this runs once a frame while painting.
    if (host.lastElementChild !== dropLine) host.appendChild(dropLine);
    scheduleThumbs();
  }

  // ------------------------------------------------------------- thumbnails

  let thumbTimer = 0;
  function scheduleThumbs() {
    if (thumbTimer) return;
    thumbTimer = window.setTimeout(() => {
      thumbTimer = 0;
      drawThumbs();
    }, THUMB_DEBOUNCE_MS);
  }

  function drawThumbs() {
    const dpr = window.devicePixelRatio || 1;
    for (const layer of stack.layers()) {
      const row = rows.get(layer.id);
      // Skip layers whose pixels have not changed. Without this the panel
      // redraws every thumbnail on every recomposite, which is every reorder,
      // every visibility toggle and every frame of every stroke.
      // A monitor change alters the ratio without touching a pixel, so the
      // ratio is part of what "already drawn" means.
      if (!row || (row.drawnRev === layer.rev && row.drawnDpr === dpr)) continue;

      const w = Math.round(THUMB * dpr);
      const h = Math.round(THUMB * dpr);
      if (row.thumb.width !== w || row.thumb.height !== h) {
        row.thumb.width = w;
        row.thumb.height = h;
      }
      const ctx = row.thumb.getContext("2d");
      if (!ctx) continue;

      ctx.clearRect(0, 0, w, h);
      const src = layer.source;
      if (src.width > 0 && src.height > 0) {
        // Contain, not cover: a logo cropped to a square thumbnail is
        // unidentifiable, which defeats the point of having one.
        const scale = Math.min(w / src.width, h / src.height);
        const dw = src.width * scale;
        const dh = src.height * scale;
        ctx.drawImage(src, (w - dw) / 2, (h - dh) / 2, dw, dh);
      }
      row.drawnRev = layer.rev;
      row.drawnDpr = dpr;
    }
  }

  // ----------------------------------------------------------------- rename

  function beginRename(id: string) {
    const row = rows.get(id);
    const layer = stack.get(id);
    if (!row || !layer || renaming === id) return;

    renaming = id;
    const input = document.createElement("input");
    input.type = "text";
    input.className = "layer-rename";
    input.value = layer.name;
    input.setAttribute("aria-label", "Layer name");

    row.name.replaceChildren(input);
    input.focus();
    input.select();

    let settled = false;
    /**
     * @param save   Keep what was typed.
     * @param refocus Put focus back on the row. True when the user finished
     *   deliberately with a key; false when they finished by clicking something
     *   else, because stealing focus back from whatever they just clicked is
     *   the kind of thing that makes a panel feel like it is fighting you.
     */
    const finish = (save: boolean, refocus: boolean) => {
      if (settled) return;
      settled = true;
      renaming = null;
      const next = input.value.trim();
      // An empty name would leave a row you cannot identify or point at, so a
      // blank commit is treated as a cancel rather than rejected with an error.
      if (save && next && next !== layer.name) {
        stack.transaction("Rename layer", () => stack.rename(id, next));
      }

      // The cached name is stale by construction: render() skipped this row's
      // name for as long as the input was in it. Clearing it forces the write.
      const live = rows.get(id);
      if (live) live.shown.name = "";
      // A rename changes no pixels, so nothing recomposites and nothing calls
      // back — the panel has to redraw itself.
      render();
      if (refocus) rows.get(id)?.el.focus();
    };

    input.addEventListener("keydown", (e) => {
      // Stopped here so the panel's own arrow and Delete handling does not fire
      // while someone is typing a name containing them.
      e.stopPropagation();
      if (e.key === "Enter") finish(true, true);
      else if (e.key === "Escape") finish(false, true);
    });
    // Blur commits rather than cancels: clicking away from a field you have
    // typed into and losing the text is the more surprising of the two.
    input.addEventListener("blur", () => finish(true, false));
  }

  // ---------------------------------------------------------------- actions

  function openRowMenu(anchor: HTMLElement, id: string) {
    const layers = stack.layers();
    const index = layers.findIndex((l) => l.id === id);
    const layer = layers[index];
    if (!layer) return;

    // Panel order is reversed, so "up" in the UI is later in the array.
    const canMoveUp = index < layers.length - 1;
    const canMoveDown = index > 0;
    const merge = index > 0 ? layers[index - 1] : null;
    // Only meaningful for an artwork layer below; `mergeDown` refuses the rest
    // on its own grounds and the menu reports those separately.
    const mergePlan = merge && merge.kind === "artwork" ? planMerge(layer, merge) : null;

    // Every action lands focus on a row afterwards. The menu is the keyboard
    // path for reordering, so leaving focus on a destroyed anchor would make it
    // a path you can walk exactly once.
    const thenFocus = (layerId: string | null | undefined) => {
      if (layerId) focusRow(layerId);
      else {
        const next = stack.active();
        if (next) focusRow(next.id);
      }
    };

    /**
     * "Only these faces" — a permanent limit on where this layer shows.
     *
     * The counterpart to the face selection, and deliberately a different thing.
     * A selection is where you are working *now*: it is a tool mode, it survives
     * no reload, and it applies to whatever you paint next. This is a property of
     * the artwork — the sleeves fabric stays on the sleeves whichever tool is up,
     * and it is what somebody means by "only on the sleeves".
     *
     * Set FROM the selection rather than through a picker of its own, because
     * there is already a good picker for faces and a second one would be a second
     * place for the two to disagree. Pick the faces, then pin them to the layer.
     */
    function facesItems(id: string, layer: RuntimeLayer) {
      const limited = (layer.faces?.length ?? 0) > 0;
      const sel = opts.selection?.() ?? EMPTY;
      // `describe` names a limb by garment and knows only these two; GarmentType
      // is wider, so narrow here rather than widening the shared vocabulary for
      // a label.
      const named = garment === "pants" ? "pants" : "shirt";
      const items = [];

      if (sel.size > 0) {
        items.push({
          label: `Only ${describeSelection(sel, named)}`,
          onSelect: () => {
            stack.transaction("Limit layer to faces", () => stack.setFaces(id, [...sel]));
            thenFocus(id);
          },
        });
      } else if (!limited) {
        // Shown disabled rather than hidden: a menu whose items appear and vanish
        // teaches nothing, and the reason IS the instruction.
        items.push({
          label: "Only selected faces",
          disabled: true,
          reason: "Choose faces with the Faces tool first.",
          onSelect: () => {},
        });
      }

      if (limited) {
        items.push({
          label: `Show on all faces (now ${describeSelection(new Set(layer.faces), named)})`,
          onSelect: () => {
            stack.transaction("Lift layer face limit", () => stack.setFaces(id, []));
            thenFocus(id);
          },
        });
      }
      return items;
    }

    /*
      Weave lives in the overflow menu, not beside the colour dot.

      The dot is there because colour is the thing people change constantly and
      it earns a fourth target on the row. Weave is changed once, when the scale
      looks wrong — and a second always-visible control on every fabric row to
      serve that would cost more than it returns.
    */
    const fabric = fabricOf(layer);
    // The bottom of the stack has nothing under it, so nothing to clip to.
    const below = stack.layers()[0]?.id !== id;

    showMenu(anchor, [
      {
        label: "Rename",
        onSelect: () => beginRename(id),
      },
      /*
        Clipping, and why it is offered here rather than in the strip above.

        It is a relationship between two layers, not a value on one — and the
        thing it most often fixes is a blend mode painting flat colour over
        empty space, which is a per-layer decision made at the moment somebody
        picks Overlay and sees grey.
      */
      ...(opts.onBlur
        ? [
            {
              // Ellipsis because it opens something and asks — the convention
              // "Replace image…" above already sets.
              label: "Blur…",
              onSelect: () => opts.onBlur?.(id),
            },
          ]
        : []),
      {
        label: stack.clipBaseOf(id) ? "Stop clipping to layer below" : "Clip to layer below",
        disabled: !below,
        reason: "Nothing below to clip to.",
        onSelect: () => {
          const on = !layer.clipToBelow;
          stack.transaction(on ? "Clip to below" : "Stop clipping", () =>
            stack.setClipToBelow(id, on),
          );
        },
      },
      ...(layer.kind === "placement" && opts.onReplace
        ? [
            {
              label: "Replace image…",
              onSelect: () => opts.onReplace?.(id),
            },
          ]
        : []),
      ...(fabric && opts.onWeave
        ? [
            {
              label: "Coarser weave",
              disabled: fabric.tilePx >= MAX_TILE_PX,
              reason: "Already the largest weave.",
              onSelect: () => opts.onWeave?.(id, 1),
            },
            {
              label: "Finer weave",
              disabled: fabric.tilePx <= MIN_TILE_PX,
              reason: "Already the finest weave.",
              onSelect: () => opts.onWeave?.(id, -1),
            },
          ]
        : []),
      ...facesItems(id, layer),
      // Between the layer's own properties and the list operations: it changes
      // this layer's pixels, which is what Rename and "Only these faces" also do.
      ...(opts.layerItems?.() ?? []),
      {
        label: "Move up",
        disabled: !canMoveUp,
        reason: "Already at the top.",
        onSelect: () => {
          stack.transaction("Move layer up", () => stack.moveBy(id, 1));
          thenFocus(id);
        },
      },
      {
        label: "Move down",
        disabled: !canMoveDown,
        reason: "Already at the bottom.",
        onSelect: () => {
          stack.transaction("Move layer down", () => stack.moveBy(id, -1));
          thenFocus(id);
        },
      },
      {
        label: "Duplicate",
        onSelect: () =>
          thenFocus(stack.transaction("Duplicate layer", () => stack.duplicate(id))?.id),
      },
      {
        label: "Merge down",
        // biome-ignore lint/complexity/useOptionalChain: the null check narrows `merge`; an optional chain would not.
        disabled: !merge || merge.kind !== "artwork" || !mergePlan || !mergePlan.ok,
        /*
          Said before the click, not after it.

          `mergeDown` also refuses a hidden or fully transparent layer and an
          unclipped layer merging into a clipped one — all cases where the
          picture would change as the layer disappeared. This path throws the
          result away, so without asking `planMerge` here those refusals would
          be a menu item that does nothing at all when pressed.
        */
        reason: !merge
          ? "Nothing below."
          : merge.kind !== "artwork"
            ? `${merge.name} is an image — flatten it first.`
            : mergePlan && !mergePlan.ok
              ? mergePlan.reason
              : "",
        onSelect: () => {
          stack.transaction("Merge down", () => stack.mergeDown(id));
          thenFocus(merge?.id);
        },
      },
      {
        label: "Delete",
        danger: true,
        disabled: layers.length === 1,
        reason: "A design needs at least one layer.",
        onSelect: () => {
          stack.transaction("Delete layer", () => stack.remove(id));
          thenFocus(null);
        },
      },
    ]);
  }

  /**
   * Select a layer and put focus back on its row.
   *
   * Rows now survive a re-render, but focus still has to follow a *programmatic*
   * selection — arrow keys, or a layer that moved — or the next keystroke has no
   * row to act on.
   */
  function selectAndFocus(id: string) {
    stack.setActive(id);
    render();
    focusRow(id);
    opts.onSelect?.(id);
  }

  function focusRow(id: string) {
    rows.get(id)?.el.focus();
  }

  // ------------------------------------------------------------------ drag

  let dragId: string | null = null;
  let armTimer = 0;
  let armed: { id: string; row: HTMLElement; x: number; y: number } | null = null;
  /** Where the dragged layer would land: the row it is over, and which side. */
  let target: { id: string; above: boolean } | null = null;
  /**
   * The pointer that owns the gesture.
   *
   * A second finger landing mid-drag used to overwrite the first one's state,
   * so a two-finger touch could start a drag with one finger and finish it with
   * the other, dropping the layer somewhere nobody pointed at.
   */
  let dragPointer: number | null = null;
  /** Set while auto-scrolling, so the loop is started once and stopped once. */
  let autoScroll = 0;
  /** Pixels per frame, sign included. Read by the loop, written by each move. */
  let scrollStep = 0;
  /** Last pointer Y of the drag, so the scroll loop can re-hit-test. */
  let lastPointerY: number | null = null;

  /** The element that actually scrolls — the dock body, not the list itself. */
  function scroller(): HTMLElement {
    let el: HTMLElement | null = host;
    while (el) {
      if (el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return host;
  }

  const disarm = () => {
    if (armTimer) window.clearTimeout(armTimer);
    armTimer = 0;
    armed = null;
  };

  function beginDrag(row: HTMLElement, id: string) {
    dragId = id;
    row.classList.add("is-dragging");
  }

  function endDrag() {
    dragId = null;
    target = null;
    dragPointer = null;
    lastPointerY = null;
    stopAutoScroll();
    disarm();
    dropLine.removeAttribute("data-show");
    for (const el of host.querySelectorAll(".is-dragging")) el.classList.remove("is-dragging");
  }

  function stopAutoScroll() {
    if (autoScroll) cancelAnimationFrame(autoScroll);
    autoScroll = 0;
    scrollStep = 0;
  }

  /**
   * Scroll the list when the drag reaches its edge.
   *
   * Without this a layer can only be moved as far as the visible rows, which on
   * a phone is three or four — the reorder silently stops working at exactly
   * the stack size where reordering starts to matter.
   *
   * Speed ramps with depth into the edge zone rather than switching on, so it
   * is controllable at the boundary instead of lurching.
   */
  function updateAutoScroll(clientY: number) {
    const el = scroller();
    const box = el.getBoundingClientRect();
    const zone = Math.min(EDGE_ZONE_PX, box.height / 3);
    let speed = 0;
    if (clientY < box.top + zone) speed = -(1 - (clientY - box.top) / zone);
    else if (clientY > box.bottom - zone) speed = 1 - (box.bottom - clientY) / zone;

    if (speed === 0 || Number.isNaN(speed)) {
      stopAutoScroll();
      return;
    }

    // The step lives outside the loop, not captured inside it. A captured value
    // would freeze both the speed and the *direction* at whatever they were
    // when the pointer first entered the edge zone — so dragging back the other
    // way would keep scrolling the wrong way until the drag ended.
    scrollStep = Math.sign(speed) * Math.min(1, Math.abs(speed)) * MAX_SCROLL_PX_PER_FRAME;
    if (autoScroll) return;

    const tick = () => {
      if (!dragId || scrollStep === 0) return stopAutoScroll();
      el.scrollTop += scrollStep;
      // The row under the pointer changes as the list moves beneath it, so the
      // indicator has to be recomputed rather than left where the last
      // pointermove put it.
      if (lastPointerY !== null) refreshTarget(lastPointerY);
      autoScroll = requestAnimationFrame(tick);
    };
    autoScroll = requestAnimationFrame(tick);
  }

  /** Recompute the landing gap for a pointer position, and draw the line. */
  function refreshTarget(clientY: number) {
    const box = host.getBoundingClientRect();
    const over = document
      .elementFromPoint(box.left + box.width / 2, clientY)
      ?.closest<HTMLElement>("[data-layer]");
    const overId = over?.dataset.layer;
    if (!over || !overId) {
      target = null;
      dropLine.removeAttribute("data-show");
      return;
    }
    // Which half of the row decides which gap, so the line always sits where
    // the pointer is heading rather than snapping to one edge of the row.
    const rowBox = over.getBoundingClientRect();
    target = { id: overId, above: clientY < rowBox.top + rowBox.height / 2 };
    showDropLine(over, target.above);
  }

  /** Put the indicator in the gap the layer would drop into. */
  function showDropLine(row: HTMLElement, above: boolean) {
    // offsetTop rather than a bounding rect: the list scrolls, and the line is
    // positioned inside the scrolled content so it must scroll with it.
    const y = above ? row.offsetTop : row.offsetTop + row.offsetHeight;
    dropLine.style.top = `${y}px`;
    dropLine.setAttribute("data-show", "");
  }

  host.addEventListener("pointerdown", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-layer]");
    const id = row?.dataset.layer;
    if (!row || !id) return;

    const onGrip = Boolean((e.target as HTMLElement).closest("[data-grip]"));

    // One gesture at a time. A second finger is not a second drag.
    if (dragId || armed) return;

    if (e.pointerType === "touch") {
      dragPointer = e.pointerId;
      // Never begin immediately on touch: this gesture might be a scroll.
      armed = { id, row, x: e.clientX, y: e.clientY };
      armTimer = window.setTimeout(() => {
        if (!armed) return;
        beginDrag(armed.row, armed.id);
        armed = null;
      }, LONG_PRESS_MS);
      return;
    }

    // Mouse and pen drag from the grip only, leaving the row free to click.
    if (!onGrip) return;
    e.preventDefault();
    dragPointer = e.pointerId;
    beginDrag(row, id);
    try {
      // An enhancement, not a requirement: it keeps the drag alive when the
      // pointer leaves the panel. The window-level pointerup already covers
      // that case, so a failure here must not take the drag down with it —
      // the same call in wirePainting is wrapped for the same reason.
      host.setPointerCapture(e.pointerId);
    } catch {
      /* no capture; the drag still works through the window listener */
    }
  });

  host.addEventListener("pointermove", (e) => {
    if (dragPointer !== null && e.pointerId !== dragPointer) return;
    if (armed) {
      const moved = Math.hypot(e.clientX - armed.x, e.clientY - armed.y);
      if (moved > SCROLL_SLOP_PX) disarm(); // it was a scroll
      return;
    }
    if (!dragId) return;

    e.preventDefault();
    lastPointerY = e.clientY;
    updateAutoScroll(e.clientY);
    refreshTarget(e.clientY);
  });

  // Takes no event: where the layer lands was decided during the move, by the
  // same code that drew the indicator, so the drop cannot disagree with the
  // line the user was looking at.
  const drop = () => {
    if (armed) disarm();
    if (!dragId) return;
    const from = dragId;
    const landing = target;
    endDrag();
    if (!landing) return;

    const to = dropIndex(
      stack.layers().map((l) => l.id),
      from,
      landing.id,
      landing.above,
    );
    if (to !== null) stack.transaction("Reorder layers", () => stack.move(from, to));
  };

  /*
    Touch scrolling has to be stopped through `touchmove`, not `pointermove`.

    `pointermove` is dispatched after the browser has already decided the
    gesture is a scroll, so preventing it there is too late — the list slides
    under the finger while the row is being dragged. `touchmove` with
    `{ passive: false }` is the only handle on it, and the listener has to be
    registered non-passively up front because a browser that assumed passive
    ignores `preventDefault` for the life of the gesture.

    Scoped to an active drag, so ordinary scrolling is untouched.
  */
  host.addEventListener(
    "touchmove",
    (e) => {
      if (dragId) e.preventDefault();
    },
    { passive: false },
  );

  host.addEventListener("pointercancel", () => endDrag());
  // Capture can be lost without a pointerup — a browser gesture takes over, the
  // element is removed. Without this the panel stays stuck in drag state, and
  // the next click lands a layer somewhere.
  host.addEventListener("lostpointercapture", () => {
    if (dragId) endDrag();
  });
  // Bound on window, not on host: a pointer released outside the panel must
  // still end the drag, and one listener cannot fire twice for one release.
  window.addEventListener("pointerup", (e) => {
    if (dragPointer !== null && e.pointerId !== dragPointer) return;
    if (dragId || armed) drop();
  });

  // -------------------------------------------------------------- keyboard

  host.addEventListener("keydown", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-layer]");
    const id = row?.dataset.layer;
    if (!id || renaming) return;

    const layers = stack.layers();
    const index = layers.findIndex((l) => l.id === id);

    // Alt+arrow moves the layer; plain arrow moves the selection. Both match
    // what every list-with-reorder does, and neither needs a mouse.
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      const dir = e.key === "ArrowUp" ? 1 : -1;
      stack.transaction(dir > 0 ? "Move layer up" : "Move layer down", () => stack.moveBy(id, dir));
      focusRow(id);
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = layers[index + (e.key === "ArrowUp" ? 1 : -1)];
      if (next) selectAndFocus(next.id);
    } else if (e.key === "F2") {
      // F2 renames in Explorer, Finder and every file tree in every IDE.
      e.preventDefault();
      beginRename(id);
    } else if (e.key === "Delete" || e.key === "Backspace") {
      if (layers.length === 1) return;
      e.preventDefault();
      stack.transaction("Delete layer", () => stack.remove(id));
      // `remove` picks the neighbour to select; follow it with focus, or the
      // next keystroke lands on nothing and the keyboard path dead-ends after
      // exactly one deletion.
      const next = stack.active();
      if (next) focusRow(next.id);
    } else if (e.key === " " || e.key === "Enter") {
      // Only when the row itself has focus; the inner buttons handle their own.
      if (e.target !== row) return;
      e.preventDefault();
      // Enter on an already-selected row renames it, matching Finder. On any
      // other row it selects, because selecting is what you meant first.
      if (id === stack.active()?.id && e.key === "Enter") beginRename(id);
      else selectAndFocus(id);
    }
  });

  return { render };
}

const GRIP =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>';

/*
  A brush stroke stopped by the shape it is painting into: a circle, and a bar
  of paint that reaches its edge and goes no further.

  The first attempt was a box with two diagonals through it, which reads as "no"
  or "disabled" — the opposite of a setting that lets you paint freely. Showing
  the paint AND the boundary in one glyph is what makes it about containment
  rather than prohibition.

  Not a padlock, deliberately: "alpha lock" is jargon borrowed from other
  editors, and a lock says "you cannot change this".
*/
const INSIDE =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="8"/><path d="M6.5 10.5h11" stroke-width="3.2"/><path d="M8 14.5h8" stroke-width="3.2"/></svg>';

/*
  The Faces tool's own marquee, with the middle filled.

  Deliberately the SAME corner glyph the tool uses, because the state and the
  thing that sets it should look like one idea — you pick faces with that tool,
  then pin them here. The filled middle is what makes it a restriction rather
  than a selection: this layer shows there and nowhere else.
*/
const ONLY_FACES =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8V5a1 1 0 0 1 1-1h3"/><path d="M16 4h3a1 1 0 0 1 1 1v3"/><path d="M20 16v3a1 1 0 0 1-1 1h-3"/><path d="M8 20H5a1 1 0 0 1-1-1v-3"/><rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" stroke="none"/></svg>';

const DOTS =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>';

const EYE =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>';

const EYE_OFF =
  '<svg viewBox="0 0 24 24" class="icon icon-sm" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>';
